// Terms pop-up before a portal action (M3-F11; rows M3.47, M3.48; DC-169 / DC-170).
// Several terms show one after another ("Terms 1 of 2"); "I agree" starts unticked; when the terms need
// an OTP the server sends it to the right person and checks it. The action runs after the last one.
// useConsentPoint(): const { requirePoint, termsModal } = useConsentPoint();
//   requirePoint("afb_confirm", allotmentPaymentId, () => doIt(), { summary, fallback })
//   fallback() runs instead of nothing when the point has no terms at all (e.g. today's KYC T&C text)
import React, { useRef, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import { getPendingConsent, sendConsentOtp, acceptConsent } from "../../../Services/ConsentGate";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import { errorTost } from "../../../Services/Toast";
import { sanitizeConsentHtml } from "../../../Modals/PaymentConsent/sanitizeConsentHtml";
import OtpEntry from "./OtpEntry";
import css from "./Crm2Booking.module.css";

export function TermsGate({ point, allotmentPaymentId, items, summary = [], onDone, onClose }) {
	const [i, setI] = useState(0);
	const [agree, setAgree] = useState(false);
	const [busy, setBusy] = useState(false);
	const it = items[i];
	const body = { point, termId: it.termId, allotmentPaymentId, applicantId: it.applicantId || null, askId: it.askId || null };
	const next = () => {
		setAgree(false);
		if (i + 1 < items.length) setI(i + 1);
		else onDone();
	};
	const acceptTick = () => {
		setBusy(true);
		acceptConsent(body)
			.then(next)
			.catch((err) => errorTost(err.message))
			.finally(() => setBusy(false));
	};
	return (
		<Modal open onClose={(_, r) => r !== "backdropClick" && onClose()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
			<Box className={css.modal}>
				<div className={css.modalHeader}>
					<span>
						{it.title || "Terms and conditions"}
						{items.length > 1 ? ` · Terms ${i + 1} of ${items.length}` : ""}
					</span>
					<span className={css.modalClose} onClick={onClose} tabIndex={0}>
						<Close width={"1.2rem"} height={"1.2rem"} />
					</span>
				</div>
				<div className={css.modalContent}>
					{summary.length > 0 && (
						<div className={css.sumBox}>
							{summary.map(([k, v]) => (
								<div key={k} className={css.kv}>
									<span className={css.kvK}>{k}</span>
									<span className={css.kvV}>{v}</span>
								</div>
							))}
						</div>
					)}
					{it.askId && <div className={css.info}>VJ updated the terms for your booking. Please read and accept them.</div>}
					<div className={css.termsText} dangerouslySetInnerHTML={{ __html: sanitizeConsentHtml(it.content || "") }} />
					{it.signer && <p className={css.muted}>To be accepted by {it.signer}.</p>}
					<label className={css.tick}>
						<input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />I agree to these terms
					</label>
					{it.how === "TICK_OTP" ? (
						<OtpEntry
							key={`${it.termId}:${it.applicantId || ""}`}
							disabled={!agree}
							sendLabel="I agree · Send OTP"
							confirmLabel="Accept"
							who={it.signer}
							send={() => sendConsentOtp(body)}
							verify={(challengeId, code) => acceptConsent({ ...body, challengeId, code })}
							onVerified={next}
						/>
					) : (
						<div className={css.otpActs}>
							<button type="button" className={`${css.btn} ${css.btnP}`} disabled={!agree || busy} onClick={acceptTick}>
								{busy ? "Saving…" : "I agree & continue"}
							</button>
						</div>
					)}
				</div>
			</Box>
		</Modal>
	);
}

export default function useConsentPoint() {
	const [req, setReq] = useState(null); // { point, allotmentPaymentId, items, summary }
	const action = useRef(null);

	const requirePoint = async (point, allotmentPaymentId, run, { summary = [], fallback = null } = {}) => {
		presentLoader();
		let res;
		try {
			res = await getPendingConsent(point, allotmentPaymentId);
		} catch (err) {
			dismissLoader();
			console.error("[consent] pending not read", point, err.message);
			errorTost(err.status === 401 ? "Please log in again." : "Unable to load the terms. Please try again.");
			return;
		}
		dismissLoader();
		if (res?.blocked) {
			errorTost(res.blocked.reason || "Please accept the updated terms first");
			return;
		}
		if (!res?.items?.length) {
			if (!res?.hasTerms && fallback) fallback();
			else run();
			return;
		}
		action.current = run;
		setReq({ point, allotmentPaymentId, items: res.items, summary });
	};
	const close = () => {
		action.current = null;
		setReq(null);
	};
	const termsModal = req ? (
		<TermsGate
			point={req.point}
			allotmentPaymentId={req.allotmentPaymentId}
			items={req.items}
			summary={req.summary}
			onClose={close}
			onDone={() => {
				const run = action.current;
				close();
				if (run) run();
			}}
		/>
	) : null;
	return { requirePoint, termsModal };
}
